/**
 * Firebase Configuration
 *
 * Initializes the Firebase app and authentication service.
 * Configuration values are loaded from environment variables.
 *
 * Migration Note (2026-02-22):
 * This replaces the previous MSAL configuration per Architecture Guardrail 1-006.
 */

import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, type Auth } from 'firebase/auth';

/**
 * Firebase configuration object loaded from environment variables
 */
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
};

function isPlaceholderFirebaseValue(value: string | undefined): boolean {
  if (!value) {
    return true;
  }

  const normalized = value.trim().toLowerCase();
  return (
    normalized.startsWith('your-') ||
    normalized.includes('replace-me') ||
    normalized.includes('placeholder')
  );
}

/**
 * Validate that required Firebase configuration is present
 */
function validateConfig(): void {
  const missingVars: string[] = [];

  if (isPlaceholderFirebaseValue(firebaseConfig.apiKey)) missingVars.push('VITE_FIREBASE_API_KEY');
  if (isPlaceholderFirebaseValue(firebaseConfig.authDomain)) missingVars.push('VITE_FIREBASE_AUTH_DOMAIN');
  if (isPlaceholderFirebaseValue(firebaseConfig.projectId)) missingVars.push('VITE_FIREBASE_PROJECT_ID');

  if (missingVars.length > 0) {
    throw new Error(
      `[Firebase] Invalid Firebase environment configuration: ${missingVars.join(', ')}. ` +
        'Copy src/presentation/auth/.env.example to .env and replace the placeholder values with your Firebase project settings.'
    );
  }
}

// Validate configuration on module load
validateConfig();

// Initialize Firebase app
let app: FirebaseApp;
try {
  app = initializeApp(firebaseConfig);
  console.info('[Firebase] App initialized successfully');
} catch (error) {
  console.error('[Firebase] Failed to initialize app:', error);
  throw error instanceof Error
    ? new Error(`[Firebase] Failed to initialize app. Check the Firebase env values for this frontend. Original error: ${error.message}`)
    : error;
}

// Initialize Firebase Authentication
let auth: Auth;
try {
  auth = getAuth(app);
  console.info('[Firebase] Auth initialized successfully');
} catch (error) {
  console.error('[Firebase] Failed to initialize auth:', error);
  throw error instanceof Error
    ? new Error(`[Firebase] Failed to initialize auth. Check the Firebase env values for this frontend. Original error: ${error.message}`)
    : error;
}

export { app, auth };
